import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef
} from "react";
import styled from "styled-components";

import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { seekFor, seekTo } from "../helpers/mediaElementSeek";
import { setMediaMuted, setMediaVolume } from "../helpers/volume";
import { useChromecast } from "../hooks/useChromecast/useChromecast";
import { usePlayerStore } from "../store/usePlayerStore";

import { enterFullscreen, exitFullscreen } from "./player-ui/Fullscreen.util";

const Video = styled.video`
  height: 100%;
  outline: none;
  position: absolute;
  top: 0;
  width: 100%;
`;

const VideoElement = ({
  setVideo,
  videoId
}: {
  setVideo: Dispatch<SetStateAction<HTMLVideoElement | undefined>>;
  videoId: string;
}) => {
  const { getState, subscribe, useStoreState } = usePlayerStore();

  const device = useStoreState.device();
  const isAutoplay = useStoreState.isAutoplay();
  const isCasting = useStoreState.isCasting();
  const isPlayerSetupDone = useStoreState.isPlayerSetupDone();
  const playerElement = useStoreState.playerElement();

  const setIsMuted = useStoreState.setIsMuted();
  const setIsPaused = useStoreState.setIsPaused();
  const setIsPlaybackStarted = useStoreState.setIsPlaybackStarted();
  const setIsPlayerSetupDone = useStoreState.setIsPlayerSetupDone();
  const setIsPlayerSetupStarted = useStoreState.setIsPlayerSetupStarted();
  const setPlayer = useStoreState.setPlayer();
  const setVolume = useStoreState.setVolume();

  const videoRef = useRef<HTMLVideoElement>();

  const { loop } = useEmbedParametersContext();

  useEffect(() => {
    setIsPlayerSetupDone(false);
    setIsPlayerSetupStarted(false);
  }, [setIsPlayerSetupDone, setIsPlayerSetupStarted, videoId]);

  useChromecast({
    videoRef
  });

  const tryAgainFailedAutoplay = useCallback(
    (video: HTMLVideoElement, options: { throwError?: boolean }) => {
      video.muted = true;
      return video.play().catch(err => {
        console.error("Failed to start video after mute", err);
        video.muted = false;
        setIsPaused(true);
        setIsPlaybackStarted(false);

        if (options.throwError) {
          throw err;
        }
      });
    },
    [setIsPaused, setIsPlaybackStarted]
  );

  const playVideo = useCallback(
    async (
      video: HTMLVideoElement,
      autoplay = false,
      options: { throwError?: boolean } = {}
    ) => {
      setIsPlaybackStarted(true);
      setIsPaused(false);

      if (!getState().isPlayerSetupDone) {
        await new Promise(resolve => {
          const unsubscribe = subscribe(
            state => state.isPlayerSetupDone,
            playerSetupDone => {
              if (playerSetupDone) {
                resolve(playerSetupDone);
                unsubscribe();
              }
            }
          );
        });
      }

      return video.play().catch(err => {
        console.error("Failed to start video", err);
        if (autoplay && video && !video.muted) {
          return tryAgainFailedAutoplay(video, options);
        } else {
          if (autoplay) {
            setIsPlaybackStarted(false);
          }
          setIsPaused(true);

          if (options.throwError) {
            throw err;
          }
        }
      });
    },
    [
      getState,
      setIsPaused,
      setIsPlaybackStarted,
      subscribe,
      tryAgainFailedAutoplay
    ]
  );

  const pauseVideo = useCallback(
    (video: HTMLVideoElement) => {
      setIsPaused(true);
      video.pause();
    },
    [setIsPaused]
  );

  const setPlaybackRate = useCallback(
    (video: HTMLVideoElement, playbackRate: number) => {
      try {
        video.playbackRate = playbackRate;
      } catch (err) {
        console.error(`Failed to set playback rate to ${playbackRate}`, err);
      }
    },
    []
  );

  useEffect(() => {
    if (isAutoplay && !isCasting && !isPlayerSetupDone) {
      playVideo(videoRef.current as HTMLVideoElement, true);
    }
  }, [isAutoplay, isCasting, playVideo, isPlayerSetupDone, videoRef]);

  const handleVideoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      setVideo(node || undefined);
      videoRef.current = node || undefined;

      if (node) {
        const state = getState();
        node.muted = state.isMuted;
        node.volume = state.volume;
        node.playbackRate = state.playbackRate;
      }

      const onVolumeMutedError = ({ isMuted, volume }) => {
        setIsMuted(isMuted);
        setVolume(volume);
      };

      if (node && !isCasting) {
        setIsPaused(node.paused);
        setPlayer({
          enterFullscreen: () =>
            enterFullscreen(device.isMobile, playerElement, node),
          exitFullscreen: () => exitFullscreen(),
          pause: () => pauseVideo(node),
          play: (options?: { throwError?: boolean }) =>
            playVideo(node, false, options),
          requestPictureInPicture: () => node.requestPictureInPicture?.(),
          seekFor: amount => seekFor(node, amount),
          seekTo: newCurrentTime => seekTo(node, newCurrentTime),
          setMuted: (isMuted, persist = false) =>
            setMediaMuted(node, isMuted, persist, onVolumeMutedError),
          setPlaybackRate: playbackRate => setPlaybackRate(node, playbackRate),
          setVolume: (volume, persist = false) =>
            setMediaVolume(node, volume, persist, onVolumeMutedError),
          type: "video"
        });
      }
    },
    [
      device,
      getState,
      isCasting,
      pauseVideo,
      playVideo,
      playerElement,
      setIsMuted,
      setIsPaused,
      setPlaybackRate,
      setPlayer,
      setVideo,
      setVolume
    ]
  );

  return (
    <Video
      className="simple-video-element"
      key={videoId}
      loop={loop}
      playsInline={true}
      ref={handleVideoRef}
      tabIndex={-1}
    />
  );
};

export default VideoElement;
